import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, Users } from 'lucide-react';

type Court = {
  id: number;
  name: string;
  type: string;
  available: boolean;
};

type TimeSlot = {
  time: string;
  available: boolean;
};

type Booking = {
  courtId: number;
  date: string;
  time: string;
};

function Booking() {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [selectedCourt, setSelectedCourt] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const courts: Court[] = [
    { id: 1, name: 'Terrain 1', type: 'Couvert', available: true },
    { id: 2, name: 'Terrain 2', type: 'Extérieur', available: true },
  ];

  const baseTimeSlots: TimeSlot[] = [
    { time: '06:00', available: true },
    { time: '07:30', available: true },
    { time: '09:00', available: true },
    { time: '10:30', available: true },
    { time: '12:00', available: true },
    { time: '13:30', available: true },
    { time: '15:00', available: true },
    { time: '16:30', available: true },
    { time: '18:00', available: true },
    { time: '19:30', available: true },
    { time: '21:00', available: true },
  ];

  // Filtrer les créneaux disponibles en fonction des réservations existantes
  const timeSlots = baseTimeSlots.map(slot => ({
    ...slot,
    available: !bookings.some(
      booking => 
        booking.date === selectedDate && 
        booking.time === slot.time &&
        (selectedCourt === null || booking.courtId === selectedCourt)
    )
  }));

  const handleBooking = () => {
    if (!selectedCourt || !selectedTime) return;
    
    // Ajouter la nouvelle réservation
    const newBooking: Booking = {
      courtId: selectedCourt,
      date: selectedDate,
      time: selectedTime
    };
    
    setBookings([...bookings, newBooking]);
    
    // Réinitialiser la sélection
    setSelectedTime(null);
    
    alert(`Réservation confirmée pour le terrain ${selectedCourt} le ${selectedDate} à ${selectedTime}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Réservation de terrain</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Date Selection */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center space-x-2 mb-4">
            <CalendarIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Date</h2>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setSelectedTime(null); // Réinitialiser l'heure sélectionnée lors du changement de date
            }}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Court Selection */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Terrain</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {courts.map((court) => (
              <button
                key={court.id}
                onClick={() => {
                  setSelectedCourt(court.id);
                  setSelectedTime(null); // Réinitialiser l'heure sélectionnée lors du changement de terrain
                }}
                disabled={!court.available}
                className={`p-3 rounded-md text-left transition-colors ${
                  selectedCourt === court.id
                    ? 'bg-blue-600 text-white'
                    : court.available
                    ? 'bg-gray-100 hover:bg-gray-200'
                    : 'bg-gray-100 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="font-semibold">{court.name}</div>
                <div className="text-sm">{court.type}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Time Selection */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Horaire</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot.time}
                onClick={() => setSelectedTime(slot.time)}
                disabled={!slot.available}
                className={`p-2 rounded-md text-center transition-colors ${
                  selectedTime === slot.time
                    ? 'bg-blue-600 text-white'
                    : slot.available
                    ? 'bg-gray-100 hover:bg-gray-200'
                    : 'bg-gray-100 opacity-50 cursor-not-allowed'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Summary */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Récapitulatif</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <span className="text-gray-600">Date:</span>
            <span className="ml-2 font-semibold">{selectedDate || '---'}</span>
          </div>
          <div>
            <span className="text-gray-600">Terrain:</span>
            <span className="ml-2 font-semibold">
              {selectedCourt ? `Terrain ${selectedCourt}` : '---'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Horaire:</span>
            <span className="ml-2 font-semibold">{selectedTime || '---'}</span>
          </div>
        </div>
        <button
          onClick={handleBooking}
          disabled={!selectedCourt || !selectedTime}
          className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-md font-semibold
            hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Confirmer la réservation
        </button>
      </div>

      {/* Additional Information */}
      <div className="mt-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-3">Informations importantes</h3>
        <ul className="list-disc list-inside space-y-2 text-gray-600">
          <li>Les réservations peuvent être effectuées jusqu'à 7 jours à l'avance</li>
          <li>La durée de réservation est de 1h30</li>
          <li>En cas d'annulation, merci de nous prévenir au moins 24h à l'avance</li>
          <li>Le paiement s'effectue sur place ou via votre compte membre</li>
        </ul>
      </div>
    </div>
  );
}

export default Booking;